import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabase";
import { useDashboardCarouselSwipe } from "../../hooks/useDashboardCarouselSwipe";
import { usePermissions } from "../../hooks/usePermissions";
import { loadScopedAccountsPayableTimelogs } from "../../helpers/scopedQueries";
import { requireAccountsPayableView } from "../../helpers/financialGuards";
import { shouldSkipSupabase } from "../../helpers/guestMode";
import { computePayrollLiabilityTotals } from "../../helpers/payrollHelpers";
import { useOrganization } from "../../context/OrganizationContext";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { useHardwareBackHandler } from "../../navigation/HardwareBackContext";
import { AccountsPayableDesktop } from "./AccountsPayableDesktop";
import "../invoice/invoiceDesktop.css";
import {
  MetricRow,
  SNAPSHOT_CARD_STYLE,
} from "../dashboard/SnapshotMetricCard";
import "../KPIDashboardScreen.css";
import "../dashboard/dashboardWidget.css";

const SCREEN_STYLE = {
  minHeight: "100vh",
  padding: "24px 18px 90px",
  color: "white",
  background:
    "radial-gradient(circle at 20% 10%, rgba(201,168,76,0.12), transparent 60%), linear-gradient(180deg, rgba(7,10,16,1) 0%, rgba(4,7,12,1) 100%)",
};

const SECTION_GAP_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};

const DIVIDER_STYLE = {
  margin: "10px 0 4px",
  height: "1px",
  background: "rgba(255,255,255,0.10)",
};

const MONEY_VALUE_STYLE = {
  minWidth: "72px",
  flexShrink: 0,
  textAlign: "right",
  fontSize: "16px",
  fontWeight: 800,
  color: "#4ee2a1",
};

const TOTAL_VALUE_STYLE = {
  ...MONEY_VALUE_STYLE,
  fontSize: "18px",
  color: "#4ee2a1",
};

const EMPTY_MESSAGE_STYLE = {
  fontSize: "13px",
  color: "rgba(255,255,255,0.45)",
  padding: "8px 4px 4px",
  margin: 0,
};

function formatMoney(amount) {
  return `$${Number(amount || 0).toFixed(2)}`;
}

function MoneyValue({ amount, emphasized = false }) {
  return (
    <span style={emphasized ? TOTAL_VALUE_STYLE : MONEY_VALUE_STYLE}>
      {formatMoney(amount)}
    </span>
  );
}

export default function AccountsPayableDashboard({
  currentUser,
  onBack,
  onCarouselNavigate,
  onOpenNavigator,
}) {
  const isDesktop = useIsDesktopViewport();
  const { directReportIds } = usePermissions();
  const { settings } = useOrganization();
  const { ref: carouselRef, style: carouselTouchStyle } =
    useDashboardCarouselSwipe({
      onNavigate: onCarouselNavigate,
      onOpenNavigator,
    });

  useHardwareBackHandler(
    useCallback(() => {
      if (typeof onBack !== "function") return false;
      onBack();
      return true;
    }, [onBack]),
  );

  const [unpaidEntries, setUnpaidEntries] = useState(0);
  const [payrollLiability, setPayrollLiability] = useState(0);
  const [crewLiability, setCrewLiability] = useState([]);
  const [unpaidEntryRows, setUnpaidEntryRows] = useState([]);

  const loadAccountsPayable = useCallback(async () => {
    if (shouldSkipSupabase(currentUser)) {
      setUnpaidEntries(0);
      setPayrollLiability(0);
      setCrewLiability([]);
      setUnpaidEntryRows([]);
      return;
    }

    if (!currentUser) {
      return;
    }

    try {
      requireAccountsPayableView(currentUser);

      const data = await loadScopedAccountsPayableTimelogs(
        supabase,
        currentUser,
        { directReportIds },
      );

      const unpaidRows = (data || []).filter(
        (row) => row.payroll_status !== "paid",
      );

      setUnpaidEntries(unpaidRows.length);
      setUnpaidEntryRows(unpaidRows);

      const liabilityTotals = computePayrollLiabilityTotals(
        unpaidRows,
        settings,
      );

      setPayrollLiability(liabilityTotals.grossPay);
      setCrewLiability(liabilityTotals.byEmployee);
    } catch (error) {
      console.error("ACCOUNTS PAYABLE LOAD ERROR:", error);
    }
  }, [currentUser, directReportIds, settings]);

  useEffect(() => {
    loadAccountsPayable();
  }, [loadAccountsPayable]);

  if (isDesktop) {
    return (
      <div ref={carouselRef} style={carouselTouchStyle}>
        <AccountsPayableDesktop
          onBack={onBack}
          unpaidEntries={unpaidEntries}
          payrollLiability={payrollLiability}
          crewLiability={crewLiability}
          unpaidRows={unpaidEntryRows}
        />
      </div>
    );
  }

  return (
    <div
      ref={carouselRef}
      className="accounts-payable-dashboard"
      style={{ ...SCREEN_STYLE, ...carouselTouchStyle }}
    >
      <button
        type="button"
        className="cockpit-back-btn guide-module-back ios-interaction-guard"
        onClick={onBack}
        style={{ alignSelf: "flex-start", marginBottom: "8px" }}
      >
        Back
      </button>
      <div className="dashboard-module-header">
        <div className="dashboard-module-title">Accounts Payable</div>
        <div className="dashboard-module-subtitle">
          Financial Operations Center
        </div>
      </div>

      <div style={SECTION_GAP_STYLE}>
        <div className="kpi-snapshot-card dashboard-widget-card" style={SNAPSHOT_CARD_STYLE}>
          <MetricRow
            label="Unpaid Entries"
            value={String(unpaidEntries)}
          />
        </div>

        <div className="kpi-snapshot-card dashboard-widget-card" style={SNAPSHOT_CARD_STYLE}>
          <div className="dashboard-widget-title">Payroll Liability</div>

          {crewLiability.length === 0 ? (
            <p className="dashboard-widget-empty" style={EMPTY_MESSAGE_STYLE}>
              No payroll data.
            </p>
          ) : (
            <div className="dashboard-widget-rows">
              {crewLiability.map((employee) => (
                <MetricRow
                  key={employee.name}
                  label={employee.name}
                  valueNode={
                    <MoneyValue amount={employee.amount} />
                  }
                />
              ))}
            </div>
          )}

          <div className="dashboard-section-divider" style={DIVIDER_STYLE} />

          <MetricRow
            label="Total Liability"
            valueNode={
              <MoneyValue amount={payrollLiability} emphasized />
            }
          />
        </div>
      </div>
    </div>
  );
}
